import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import moment from 'moment';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
} from 'common/dataTableConstants';

import {
  formatAgoDate,
  formatPhoneNumber,
} from 'common/formatters';

import {
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';

import SrBox from 'client/components/SrBox';
import SrHouseholdListingChangeListingModal from 'client/components/SrHouseholdListingChangeListingModal';
import SrHouseholdPrequalSummary from 'client/components/SrHouseholdPrequalSummary';
import SrAdminHouseholdNotes from 'client/components/SrAdminHouseholdNotes/SrAdminHouseholdNotes';
import SrAdminPartnerNotes from 'client/components/SrAdminPartnerNotes/SrAdminPartnerNotes';
import SrProgressBarPercent, {
  SR_PROGRESS_BAR_THEME_ROUNDED_BLACK,
} from 'client/components/SrProgressBarPercent/SrProgressBarPercent';
import SrInitialsCircle, {
  SR_INITIALS_CIRCLE_THEME_YELLOW,
  SR_INITIALS_CIRCLE_THEME_PURPLE,
} from 'client/components/SrInitialsCircle/SrInitialsCircle';
import {
  getNextStepId,
} from 'client/components/SrApplicationAssemblyLine/assemblyLineStepIds';

import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrApplicantCard, {
  SR_APPLICANT_CARD_THEME_LIGHT,
  SR_APPLICANT_CARD_THEME_DARK,
} from 'client/components/SrApplicantCard/SrApplicantCard';
import SrApplicationAssemblyLineSteps from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineSteps';
import SrRenterHouseholdsModal from 'client/components/SrRenterHouseholdsModal';
import SrApplicationAssemblyLineEditStatusModal from 'client/components/SrApplicationAssemblyLineEditStatusModal/SrApplicationAssemblyLineEditStatusModal';
import SrApplicationAssemblyLineNextHouseholdButton from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineNextHouseholdButton';

import './SrApplicationAssemblyLine.less';

import {
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
} from 'common/householdConstants';

import {
  makeApplicationStatusList,
  getHouseholdEarliestApplicationStatus,
} from 'common/householdApplication/applicationHelpers';

const householdAcceptedAplicationStatusList = makeApplicationStatusList({
  fromApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
});

const SrApplicationAssemblyLine = ({
  assemblyLine,
  dispatch,
  currentUser,
  reloadAssemblyLineData,
  startAssemblyLineApplication,
  updateAssemblyLineApplication,
}) => {
  useEffect(() => {
    const timer = setInterval(reloadAssemblyLineData, 3000);
    return () => clearInterval(timer);
  }, [reloadAssemblyLineData]);

  const [showChangeListingModal, setShowChangeListingModal] = useState(false);
  const [showHouseholdRenterId, setShowHouseholdRenterId] = useState(null);
  const [
    assemblyLineApplicationEditStatusModalProps,
    setAssemblyLineApplicationEditStatusModalProps,
  ] = useState(null);

  const [applicantUserId, setApplicantUserId] = useState(assemblyLine.leadRenterId);

  const allApplications = [
    ...assemblyLine.householdApplications,
    ...assemblyLine.guarantorApplications,
  ];
  const allApplicants = [
    ...assemblyLine.renters,
    ...assemblyLine.guarantors,
  ];

  const householdApplication = allApplications.find((application) => {
    return application.userId === applicantUserId;
  });

  const applicant = allApplicants.find((user) => {
    return applicantUserId === user.id;
  });

  useEffect(() => {
    if (!householdApplication.assemblyLineApplication) {
      startAssemblyLineApplication({
        householdApplicationId: householdApplication.id,
      });
    }
  }, [
    householdApplication,
    startAssemblyLineApplication,
  ]);

  const onToggleChangeListingModal = useCallback(() => {
    setShowChangeListingModal(!showChangeListingModal);
  }, [showChangeListingModal, setShowChangeListingModal]);

  const duration = moment.duration(moment().diff(moment(assemblyLine.processingTime.startDate)));
  const days = duration ? Math.floor(duration.asDays()) : null;
  const hours = duration ? Math.floor(duration.hours()) : null;

  const processingDurationText = days && hours
    ? `Processing ${days} ${pluralize('day', days)}, ${hours} ${pluralize('hour', hours)} ago`
    : days
      ? `Processing ${days} ${pluralize('day', days)} ago`
      : assemblyLine.processingTime.startDate
        ? `Processing ${formatAgoDate(assemblyLine.processingTime.startDate)}`
        : 'Not Processing';

  const processingColorYellowDate = moment(assemblyLine.processingTime.startDate).add(1, 'days');
  const processingColorRedDate = moment(assemblyLine.processingTime.startDate).add(3, 'days');

  const processingDurationColor = !assemblyLine.processingTime.startDate
    ? 'black'
    : moment().isBefore(processingColorYellowDate)
      ? 'green'
      : moment().isBefore(processingColorRedDate)
        ? 'yellowGold'
        : 'red';

  const reloadDataTableHousehold = (householdId) => {
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
      viewId: `household-id-${householdId}`,
      filter: {
        'households.id': householdId,
      },
      count: 1,
    });
  };

  const onEditAssemblyLineApplicationFields = ({ assemblyLineApplication, fields, fieldId }) => {
    setAssemblyLineApplicationEditStatusModalProps({
      assemblyLineApplication,
      fields,
      fieldId,
    });
  };

  const onCloseAssemblyLineApplicationFieldsModal = () => {
    setAssemblyLineApplicationEditStatusModalProps(null);
  };

  const editAppLink = (
    `/rental-application/${householdApplication.userId}/${assemblyLine.listing.id}/form`
  );

  const householdApplicationStatus = getHouseholdEarliestApplicationStatus(
    assemblyLine.householdApplications
  );
  const householdAccepted = householdAcceptedAplicationStatusList.includes(householdApplicationStatus);

  const hasBuyersAgent = assemblyLine.householdListing.hasBuyersAgent;
  const buyersAgentName = assemblyLine.householdListing.buyersAgentName;
  const buyersAgentPhone = assemblyLine.householdListing.buyersAgentPhone;
  const buyersAgentEmail = assemblyLine.householdListing.buyersAgentEmail;

  return (
    <>
      <div styleName='SrApplicationAssemblyLine'>
        <SrBox backgroundColor='white' height='100vh' overflowY='auto'>
          <SrBox borderBottom='2px solid #EAEBEC' padding='40px 16px 16px'>
            <SrText
              font='greycliff'
              color='blueGreycliff'
              weight='800'
              size='18'
              lineHeight='24px'
              display='block'
            >
              {assemblyLine.listing.addressStreetDisplay}
            </SrText>
            <SrFlex marginTop='6px' align='center'>
              {assemblyLine.renters.map((renter, index) => (
                <SrBox
                  key={renter.id}
                  marginLeft={index > 0 ? '-9px' : '-4px'}
                  zIndex={assemblyLine.renters.length - index}
                >
                  <SrInitialsCircle
                    name={renter.name}
                    theme={index % 2 === 0
                      ? SR_INITIALS_CIRCLE_THEME_PURPLE
                      : SR_INITIALS_CIRCLE_THEME_YELLOW
                    }
                    hasBorder
                  />
                </SrBox>
              ))}
              <SrBox marginLeft='12px' width='220px'>
                <SrProgressBarPercent
                  percent={assemblyLine.status.progressPercent * 100}
                  theme={SR_PROGRESS_BAR_THEME_ROUNDED_BLACK}
                />
              </SrBox>
            </SrFlex>
            <SrBox marginTop='6px'>
              <SrText
                font='greycliff'
                color={processingDurationColor}
                weight='800'
                size='18'
                lineHeight='24px'
                display='block'
              >
                {processingDurationText}
              </SrText>
            </SrBox>
            <SrBox marginTop='16px'>
              {assemblyLine.listing.mlsNumber && (
                <SrText
                  font='greycliff'
                  color='grey'
                  size='14'
                  lineHeight='24px'
                  weight='600'
                  display='block'
                >
                  MLS: {assemblyLine.listing.mlsNumber}
                </SrText>
              )}
              {assemblyLine.partner && (
                <SrLink to={`/partner/${assemblyLine.partner.slug}`} target='_blank'>
                  <SrText
                    font='greycliff'
                    color='blue300'
                    size='14'
                    lineHeight='24px'
                    weight='600'
                    display='block'
                  >
                    {assemblyLine.partner.displayName}
                  </SrText>
                </SrLink>
              )}
              {assemblyLine.partner && (
                <SrLink
                  to={`/partner/${assemblyLine.partner.slug}/listing/${assemblyLine.listing.id}`}
                  target='_blank'
                >
                  <SrText
                    font='greycliff'
                    color='blue300'
                    size='14'
                    lineHeight='24px'
                    weight='600'
                    display='block'
                  >
                    Partner Listing
                  </SrText>
                </SrLink>
              )}
              <SrLink to={`/admin/listings/edit/${assemblyLine.listing.id}`} target='_blank'>
                <SrText
                  font='greycliff'
                  color='blue300'
                  size='14'
                  lineHeight='24px'
                  weight='600'
                  display='block'
                >
                  CLP
                </SrText>
              </SrLink>
              <SrText
                font='greycliff'
                color='blue300'
                size='14'
                lineHeight='24px'
                weight='600'
                display='block'
                cursor='pointer'
                onClick={() => onToggleChangeListingModal()}
              >
                Change Listing
              </SrText>
              <SrText
                font='greycliff'
                color='blue300'
                size='14'
                lineHeight='24px'
                weight='600'
                display='block'
                cursor='pointer'
                onClick={() => {
                  setShowHouseholdRenterId(assemblyLine.leadRenterId);
                }}
              >
                Legacy Household Page
              </SrText>
            </SrBox>
          </SrBox>
          {hasBuyersAgent && buyersAgentName && (
            <SrBox padding='16px' borderBottom='2px solid #EAEBEC'>
              <SrBox marginBottom='10px'>
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  weight='800'
                  size='18'
                  lineHeight='24px'
                  display='block'
                >
                  Buyer-Side Agent
                </SrText>
              </SrBox>
              <SrText size='14' weight='600' lineHeight='17px' color='grey'>
                {buyersAgentName} <SrBox height='4px' />
                {buyersAgentPhone && formatPhoneNumber(buyersAgentPhone)}
                {buyersAgentEmail && buyersAgentEmail ? ' | ' : ''}
                {buyersAgentEmail}
              </SrText>
            </SrBox>
          )}
          <SrBox padding='16px' borderBottom='2px solid #EAEBEC'>
            <SrBox marginBottom='10px'>
              <SrText
                font='greycliff'
                color='blueGreycliff'
                weight='800'
                size='18'
                lineHeight='24px'
                display='block'
              >
                Applicants
              </SrText>
            </SrBox>
            <SrBox padding='8px 0'>
              {assemblyLine.status.stepsByApplicants.map((step, index) => (
                <SrBox key={step.renterId} marginTop={index > 0 ? '16px' : null}>
                  <SrApplicantCard
                    name={step.renterName}
                    applicantTitle={step.applicantTitle}
                    phone={step.renterPhone}
                    email={step.renterEmail}
                    applicationStatus={step.applicationStatus}
                    progressPercent={step.progressPercent * 100}
                    onClick={applicantUserId === step.renterId
                      ? null
                      : () => setApplicantUserId(step.renterId)
                    }
                    theme={applicantUserId === step.renterId
                      ? SR_APPLICANT_CARD_THEME_DARK
                      : SR_APPLICANT_CARD_THEME_LIGHT
                    }
                  />
                </SrBox>
              ))}
            </SrBox>
          </SrBox>
          {assemblyLine.householdQualification && (
            <SrBox padding='16px 16px 16px'>
              <SrBox>
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  weight='800'
                  size='18'
                  lineHeight='24px'
                  display='block'
                >
                  Household Summary
                </SrText>
              </SrBox>
              {assemblyLine.activeApps > 0 && (
                <SrBox marginTop='5px'>
                  <SrText weight='800' fontSize='16px' color='yellowGold'>
                    This household has applied to other listings
                  </SrText>
                </SrBox>
              )}
              <SrHouseholdPrequalSummary
                partner={assemblyLine.partner}
                currentUser={currentUser}
                listing={assemblyLine.listing}
                householdListing={assemblyLine.householdListing}
                tenantScreeningCriteria={{
                  ...assemblyLine.tenantScreeningCriteria,
                  breedRestrictions: assemblyLine.breedRestrictions,
                }}
                rentersQualifications={assemblyLine.householdQualification.rentersQualifications}
                householdRequirements={assemblyLine.householdQualification.householdRequirements}
                rentersVerifications={assemblyLine.householdQualification.rentersVerifications}
                guarantorsVerifications={assemblyLine.householdQualification.guarantorsVerifications}
                householdVerifications={assemblyLine.householdQualification.householdVerifications}
                householdApplications={assemblyLine.householdApplications}
                guarantorApplications={assemblyLine.guarantorApplications}
                tenantAlertReports={assemblyLine.tenantAlertReports}
                displayTitle={false}
                householdAccepted={householdAccepted}
              />
            </SrBox>
          )}
        </SrBox>
        <SrBox
          margin='16px 0'
          backgroundColor='white'
          borderRadius='14px'
          height='100vh'
          overflowY='auto'
          display='flex'
          flexDirection='column'
        >
          <SrFlex
            align='center'
            justify='between'
            borderBottom='1px solid #CED3DA'
            minHeight='74px'
            boxSizing='border-box'
            padding='0 24px'
          >
            <SrText
              font='greycliff'
              color='blueGreycliff'
              weight='700'
              size='21'
              lineHeight='25px'
              display='block'
            >
              {`${householdApplication.firstName} ${householdApplication.lastName}`}
            </SrText>
            <SrBox>
              <SrLink
                target='_blank'
                to={editAppLink}
              >
                <SrText
                  font='greycliff'
                  color='blue300'
                  weight='700'
                  size='14'
                  lineHeight='17px'
                >
                  Edit App
                </SrText>
              </SrLink>
            </SrBox>
          </SrFlex>
          <SrApplicationAssemblyLineSteps
            assemblyLine={assemblyLine}
            reloadAssemblyLineData={reloadAssemblyLineData}
            householdApplication={householdApplication}
            applicant={applicant}
            updateAssemblyLineApplication={updateAssemblyLineApplication}
            initialStepExpanded={getNextStepId({
              assemblyLine,
              householdApplication,
            })}
            onEditAssemblyLineApplicationFields={onEditAssemblyLineApplicationFields}
          />
          <SrBox minHeight='50px' />
          <SrApplicationAssemblyLineNextHouseholdButton
            applicantUserId={applicantUserId}
            applicants={assemblyLine.status.stepsByApplicants}
            updateApplicantUserId={setApplicantUserId}
            householdListingId={assemblyLine.householdListing.id}
          />
          <SrBox minHeight='50px' />
        </SrBox>
        <SrBox padding='16px 16px 0 0' height='calc(100vh - 32px)' overflowY='hidden'>
          <SrAdminHouseholdNotes
            householdId={assemblyLine.id}
            dispatch={dispatch}
            currentUser={currentUser}
            height='calc(50vh - 16px)'
          />
          <SrAdminPartnerNotes
            partnerId={assemblyLine.partner?.id}
            dispatch={dispatch}
            currentUser={currentUser}
            height='50vh'
          />
        </SrBox>
      </div>
      {showChangeListingModal && (
        <SrHouseholdListingChangeListingModal
          household={{
            ...assemblyLine,
            listingApplications: assemblyLine.householdApplications.map((app) => {
              return {
                ...app,
                listing: assemblyLine.listing,
              };
            }),
          }}
          householdListing={{
            ...assemblyLine.householdListing,
            listing: assemblyLine.listing,
            applications: assemblyLine.householdApplications,
            householdListingId: assemblyLine.householdListing.id,
            listingId: assemblyLine.listing.id,
          }}
          dispatch={dispatch}
          onUpdateHousehold={() => {
            return reloadAssemblyLineData();
          }}
          onClose={() => onToggleChangeListingModal()}
        />
      )}
      {showHouseholdRenterId && (
        <SrRenterHouseholdsModal
          renter={{ id: showHouseholdRenterId }}
          onClose={() => {
            setShowHouseholdRenterId(null);
            reloadAssemblyLineData();
          }}
          householdAdminsList={[]}
          reloadDataTableHousehold={reloadDataTableHousehold}
        />
      )}
      {assemblyLineApplicationEditStatusModalProps && (
        <SrApplicationAssemblyLineEditStatusModal
          assemblyLineApplication={assemblyLineApplicationEditStatusModalProps.assemblyLineApplication}
          fields={assemblyLineApplicationEditStatusModalProps.fields}
          updateAssemblyLineApplication={updateAssemblyLineApplication}
          onClose={onCloseAssemblyLineApplicationFieldsModal}
          editAppLink={editAppLink}
        />
      )}
    </>
  );
};

SrApplicationAssemblyLine.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  reloadAssemblyLineData: PropTypes.func.isRequired,
  updateAssemblyLineApplication: PropTypes.func.isRequired,
};

SrApplicationAssemblyLine.displayName = 'SrApplicationAssemblyLine';

export default SrApplicationAssemblyLine;
